import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { getCalendarMatrix } from '../../utils/dateUtils';
import CalendarNavBar from '../CalendarNavBar/CalendarNavBar';
import CalendarBody from '../CalendarBody/CalendarBody';

const EMPTY_DATES: any[] = [];

interface ICalendarProps {
  date?: string;
  blockedOutDates?: any[];
  datesWithBookings?: any[];
  datesWithInstantBook?: any[];
  datesWithHolds?: any[];
  selectedDates?: string[];
  className?: string;
  disablePastDate?: boolean;
  enablePastDates?: boolean;
  disableUniformHeight?: boolean;
  dateFormatFn?: (e: Dayjs) => string;
  onDateSelect?: (e?: any) => void;
  onCurrentDateChange?: (e?: any) => void;
}

export interface ICalendarMatrix {
  Su: string[];
  Mo: string[];
  Tu: string[];
  We: string[];
  Th: string[];
  Fr: string[];
  Sa: string[];
}

const Calendar: React.FC<React.PropsWithChildren<ICalendarProps>> = ({
  date,
  selectedDates = EMPTY_DATES,
  blockedOutDates = EMPTY_DATES,
  datesWithBookings = EMPTY_DATES,
  datesWithInstantBook = EMPTY_DATES,
  datesWithHolds = EMPTY_DATES,
  className,
  onDateSelect,
  enablePastDates,
  disableUniformHeight,
  dateFormatFn = (d) => d.format('YYYY-MM-DD'),
  onCurrentDateChange,
}) => {
  const [currentDate, setCurrentDate] = React.useState<Dayjs>();

  const [calendarMatrix, setCalendarMatrix] = React.useState<ICalendarMatrix>();
  const [currentDateSplit, setCurrentDateSplit] = React.useState<{
    month: string;
    year: string;
  }>();

  const selectedDatesConvertedToDayJs = React.useMemo(
    () => selectedDates.map((date) => dayjs(date)),
    [selectedDates]
  );
  const blokedOutDatesConvertedToDayJs = React.useMemo(
    () => blockedOutDates.map((date) => dayjs(date)),
    [blockedOutDates]
  );
  const datesWithBookingsConvertedToDayjs = React.useMemo(
    () => datesWithBookings.map((date) => dayjs(date)),
    [datesWithBookings]
  );
  const datesWithInstantBookConvertedToDayjs = React.useMemo(
    () => datesWithInstantBook.map((date) => dayjs(date)),
    [datesWithInstantBook]
  );
  const datesWithHoldsConvertedToDayjs = React.useMemo(
    () => datesWithHolds.map((date) => dayjs(date)),
    [datesWithHolds]
  );

  React.useEffect(() => {
    let currentDate = dayjs();
    if (date) {
      currentDate = dayjs(date);
    }
    setCurrentDate(currentDate);
  }, [date]);

  React.useEffect(() => {
    if (currentDate) {
      const calendarMatrix = getCalendarMatrix(currentDate);
      setCalendarMatrix(calendarMatrix);
      const curMonth = currentDate.format('MMMM');
      const curYear = currentDate.format('YYYY');
      setCurrentDateSplit({ month: curMonth, year: curYear });

      if (!currentDate.isSame(dayjs(date), 'day')) {
        onCurrentDateChange?.(currentDate.format('YYYY-MM-DD'));
      }
    }
  }, [currentDate]);

  function handleNextMonthSelect(e) {
    e.stopPropagation();
    const newDate = currentDate?.add(1, 'month');
    setCurrentDate(newDate);
  }

  function handlePrevMonthSelect(e) {
    e.stopPropagation();
    const newDate = currentDate?.subtract(1, 'month');
    setCurrentDate(newDate);
  }

  function handleNextYearSelect() {
    const newDate = currentDate?.add(1, 'year');
    setCurrentDate(newDate);
  }

  function handlePrevYearSelect() {
    const newDate = currentDate?.subtract(1, 'year');
    setCurrentDate(newDate);
  }

  function handleDateSelect(_e, date?: string) {
    const dateToFormat = (currentDate as Dayjs)?.set?.('date', +(date || ''));
    onDateSelect?.(dateFormatFn(dateToFormat));
  }

  if (!calendarMatrix) {
    return null;
  }
  return (
    <div className={className}>
      <section className="mb-2">
        <CalendarNavBar
          {...(currentDateSplit || {})}
          onNextMonth={handleNextMonthSelect}
          onPrevMonth={handlePrevMonthSelect}
          onNextYear={handleNextYearSelect}
          onPrevYear={handlePrevYearSelect}
        />
      </section>
      <CalendarBody
        calendarMatrix={calendarMatrix}
        currentDate={currentDate || dayjs()}
        selectedDates={selectedDatesConvertedToDayJs}
        blockedOutDates={blokedOutDatesConvertedToDayJs}
        onDateSelect={handleDateSelect}
        disablePastDates={!enablePastDates}
        disableUniformHeight={disableUniformHeight}
        datesWithBookings={datesWithBookingsConvertedToDayjs}
        datesWithInstantBook={datesWithInstantBookConvertedToDayjs}
        datesWithHolds={datesWithHoldsConvertedToDayjs}
      />
    </div>
  );
};
export default Calendar;
